/**
 * Step editor panel (AR-328).
 *
 * One shell, five bodies. Placement & visibility, the rule builder and the
 * emails panel are identical for every type; only the middle block changes with
 * the step type. System steps reuse the same shell read-only, so a locked step
 * sits in the same place in the flow an editable one would.
 */
import React, { useMemo, useState } from 'react';
import {
  ArrowRight,
  Lock,
  Paperclip,
  Play,
  Plus,
  RectangleHorizontal,
  Search,
  Sparkles,
  Type,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ACTIVATION_PATHWAYS,
  ACTIVATION_PLANS,
  ACTIVATION_ROUTES,
  PROFILE_FIELD_VARIABLES,
  REQUIREMENT_LABELS,
  STEP_TYPE_STYLES,
  contentBlocksForStep,
  plansForStep,
  visibilityRulesForStep,
  type ActivationModule,
  type ActivationRequirement,
  type ActivationStep,
  type ContentBlockKind,
} from '@/lib/activationBuilder';
import {
  FieldLabel,
  PanelSectionLabel,
  ReadOnlyBox,
  TypeBadge,
  VariablePill,
} from './activationPrimitives';
import { StepEmailsPanel } from './StepEmailsPanel';
import { VisibilityRuleBuilder } from './VisibilityRuleBuilder';

interface ActivationStepEditorProps {
  step: ActivationStep;
  modules: ActivationModule[];
  onCancel: () => void;
  onSave: () => void;
  onArchive: () => void;
}

const REQUIREMENTS: ActivationRequirement[] = ['required', 'skippable', 'optional'];

const BLOCK_ICONS: Record<ContentBlockKind, React.ComponentType<{ className?: string }>> = {
  video: Play,
  text: Type,
  file: Paperclip,
  cta: RectangleHorizontal,
};

/* ── Small shared controls ───────────────────────────────────────────────── */

const CheckChip: React.FC<{ label: string; checked: boolean; disabled?: boolean }> = ({
  label,
  checked,
  disabled,
}) => (
  <label
    className={cn(
      'inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-[11.5px] font-medium',
      checked
        ? 'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-900 dark:bg-orange-950/40 dark:text-orange-300'
        : 'border-gray-200 bg-white text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400',
      disabled ? 'cursor-not-allowed' : 'cursor-pointer',
    )}
  >
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      readOnly
      className="h-3.5 w-3.5 shrink-0 accent-orange-500"
    />
    {label}
  </label>
);

const RequirementToggle: React.FC<{
  value: ActivationRequirement;
  disabled?: boolean;
}> = ({ value, disabled }) => (
  <div className="inline-flex rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
    {REQUIREMENTS.map((option) => (
      <button
        key={option}
        type="button"
        disabled={disabled}
        aria-pressed={value === option}
        className={cn(
          'rounded-md px-3 py-1 text-[10.5px] font-semibold',
          value === option
            ? 'bg-white text-orange-700 shadow-sm dark:bg-gray-900 dark:text-orange-300'
            : 'text-gray-400 dark:text-gray-500',
          disabled && 'cursor-not-allowed',
        )}
      >
        {REQUIREMENT_LABELS[option]}
      </button>
    ))}
  </div>
);

/** Searchable `{{variable}}` picker used by the AI step panel. */
const ProfileFieldPicker: React.FC<{ disabled?: boolean }> = ({ disabled }) => {
  const [query, setQuery] = useState('');
  const matches = useMemo(
    () =>
      PROFILE_FIELD_VARIABLES.filter((v) =>
        v.toLowerCase().includes(query.trim().toLowerCase()),
      ).slice(0, 8),
    [query],
  );

  return (
    <div>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
        <Input
          value={query}
          disabled={disabled}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search profile fields…"
          aria-label="Search profile fields"
          className="h-8 pl-8 text-xs"
        />
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {matches.length ? (
          matches.map((variable) => <VariablePill key={variable}>{variable}</VariablePill>)
        ) : (
          <span className="text-[11px] text-gray-400">No profile field matches “{query}”.</span>
        )}
      </div>
    </div>
  );
};

/* ── Type-specific bodies ────────────────────────────────────────────────── */

const SimpleBody: React.FC<{ step: ActivationStep }> = ({ step }) => (
  <section>
    <PanelSectionLabel>Content blocks</PanelSectionLabel>
    <ul className="space-y-2">
      {contentBlocksForStep(step).map((block) => {
        const BlockIcon = BLOCK_ICONS[block.kind];
        return (
        <li
          key={block.name}
          className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 dark:border-gray-700 dark:bg-gray-900"
        >
          <span
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white',
              STEP_TYPE_STYLES[block.type].tile,
            )}
            aria-hidden
          >
            <BlockIcon className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">{block.name}</p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">{block.detail}</p>
          </div>
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-orange-600 dark:text-orange-400">
            Edit
          </Button>
        </li>
        );
      })}
    </ul>
  </section>
);

const FormBody: React.FC<{ step: ActivationStep }> = ({ step }) => (
  <section>
    <PanelSectionLabel>Form fields &amp; profile mapping</PanelSectionLabel>
    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
      <table className="w-full min-w-[420px] text-left text-xs">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr className="font-mono text-[10px] uppercase tracking-wider text-gray-400">
            <th className="px-3 py-2 font-semibold">Field</th>
            <th className="px-3 py-2 font-semibold">→ Profile field</th>
            <th className="px-3 py-2 font-semibold">Req</th>
          </tr>
        </thead>
        <tbody>
          {(step.fields ?? []).map((field) => (
            <tr
              key={field.map}
              className="border-t border-gray-100 bg-white dark:border-gray-800 dark:bg-gray-900"
            >
              <td className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{field.label}</td>
              <td className="px-3 py-2.5 font-mono text-[11px] text-gray-600 dark:text-gray-300">
                {field.map}
              </td>
              <td
                className={cn(
                  'px-3 py-2.5 text-[10px] font-bold',
                  field.required ? 'text-orange-700 dark:text-orange-400' : 'text-gray-400',
                )}
              >
                {field.required ? 'Yes' : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <Button variant="ghost" size="sm" className="mt-2 h-7 px-2 text-xs text-orange-600 dark:text-orange-400">
      <Plus className="mr-1 h-3 w-3" /> Add field
    </Button>
  </section>
);

const AiFormBody: React.FC<{ step: ActivationStep }> = ({ step }) => (
  <section className="space-y-4">
    <PanelSectionLabel>AI-generative form action</PanelSectionLabel>

    <div>
      <FieldLabel>Member seed inputs</FieldLabel>
      <div className="flex flex-wrap gap-1.5">
        {(step.seeds ?? []).map((seed) => (
          <span
            key={seed}
            className="rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-[11px] text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
          >
            {seed}
          </span>
        ))}
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-orange-600 dark:text-orange-400">
          <Plus className="mr-1 h-3 w-3" /> Add seed
        </Button>
      </div>
    </div>

    <div>
      <FieldLabel>System prompt</FieldLabel>
      <Textarea defaultValue={step.aiPrompt} rows={3} className="text-xs" />
    </div>

    <div>
      <FieldLabel>Insert a profile field</FieldLabel>
      <ProfileFieldPicker />
      {!!step.aiVariables?.length && (
        <>
          <p className="mb-1.5 mt-3 text-[11px] text-gray-500 dark:text-gray-400">
            Variables used by this prompt
          </p>
          <div className="flex flex-wrap gap-1.5">
            {step.aiVariables.map((variable) => (
              <VariablePill key={variable}>{variable}</VariablePill>
            ))}
          </div>
        </>
      )}
    </div>

    <div>
      <FieldLabel>Output target field</FieldLabel>
      <Input defaultValue={step.aiOutput} className="h-8 max-w-sm font-mono text-xs" />
    </div>

    <div className="rounded-xl border border-teal-200 bg-teal-50/60 p-3 dark:border-teal-900 dark:bg-teal-950/20">
      <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold text-teal-800 dark:text-teal-300">
        <Sparkles className="h-3.5 w-3.5" /> Member-side preview
      </p>
      <ol className="flex flex-wrap items-center gap-2 text-[11px] text-teal-800 dark:text-teal-300">
        {['Generate', 'Review / edit', 'Save to profile'].map((phase, index) => (
          <li key={phase} className="flex items-center gap-2">
            <span className="rounded-md border border-teal-200 bg-white px-2 py-1 font-semibold dark:border-teal-800 dark:bg-teal-950/50">
              {index + 1}. {phase}
            </span>
            {index < 2 && <ArrowRight className="h-3 w-3 opacity-60" aria-hidden />}
          </li>
        ))}
      </ol>
      <Button variant="outline" size="sm" className="mt-3 h-7 text-xs">
        Preview as member
      </Button>
    </div>
  </section>
);

const AddOnBody: React.FC<{ step: ActivationStep }> = ({ step }) => (
  <section className="space-y-3">
    <PanelSectionLabel>Add-on product</PanelSectionLabel>

    <div>
      <FieldLabel>Title</FieldLabel>
      <Input defaultValue="Add the Accelerator Course?" className="h-9 text-xs" />
    </div>
    <div>
      <FieldLabel>Description</FieldLabel>
      <Textarea
        defaultValue="A guided 6-week program that shortens the ramp to a first booking."
        rows={2}
        className="text-xs"
      />
    </div>
    <div>
      <FieldLabel>Value-add statement</FieldLabel>
      <Input defaultValue="Waive the $99 activation fee when added today." className="h-9 text-xs" />
    </div>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div>
        <FieldLabel>Cost display</FieldLabel>
        <Input defaultValue="$350 one-time" className="h-9 text-xs" />
      </div>
      <div>
        <FieldLabel>Stripe product / price ID</FieldLabel>
        <Input defaultValue="price_ACCEL_350" className="h-9 font-mono text-xs" />
      </div>
      <div>
        <FieldLabel>Coupon</FieldLabel>
        <Input defaultValue="ACCEL-LAUNCH" className="h-9 font-mono text-xs" />
      </div>
      <div>
        <FieldLabel>Placement</FieldLabel>
        <Select defaultValue="checkout">
          <SelectTrigger className="h-9 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="checkout">Inline in Checkout</SelectItem>
            <SelectItem value="activation">Inline in Activation</SelectItem>
            <SelectItem value="post">Post-activation offer</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    <div>
      <FieldLabel>Promo video (optional)</FieldLabel>
      <Input
        defaultValue={step.video ? 'https://videos.worldvia.com/accelerator-promo' : ''}
        placeholder="Paste an embed URL"
        className="h-9 text-xs"
      />
    </div>
  </section>
);

const SystemBody: React.FC<{ step: ActivationStep }> = ({ step }) => (
  <section className="space-y-3">
    <PanelSectionLabel>System behavior</PanelSectionLabel>
    {step.systemFields && (
      <div>
        <FieldLabel>System fields</FieldLabel>
        <ReadOnlyBox value={step.systemFields} />
      </div>
    )}
    {step.systemLogic && (
      <div>
        <FieldLabel>System logic</FieldLabel>
        <ReadOnlyBox value={step.systemLogic} />
      </div>
    )}
    {!step.systemFields && !step.systemLogic && (
      <p className="text-xs text-gray-400 dark:text-gray-500">
        No system fields or logic recorded for this step.
      </p>
    )}
  </section>
);

/* ── Panel ───────────────────────────────────────────────────────────────── */

export const ActivationStepEditor: React.FC<ActivationStepEditorProps> = ({
  step,
  modules,
  onCancel,
  onSave,
  onArchive,
}) => {
  const locked = !!step.locked;
  const plans = plansForStep(step);
  // Seeded once per step — the page keys this panel by step, so selecting a
  // different step mounts a fresh draft rather than carrying this one over.
  const [rules, setRules] = useState(() => visibilityRulesForStep(step));
  const [join, setJoin] = useState<'AND' | 'OR'>('AND');

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
      {/* Header */}
      <div className="flex flex-wrap items-start gap-3 border-b border-gray-200 px-6 py-5 dark:border-gray-700">
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <TypeBadge type={step.type} />
            {locked && (
              <span className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-violet-700 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300">
                <Lock className="h-2.5 w-2.5" /> System / Locked
              </span>
            )}
          </div>
          <h3 className="truncate text-xl font-semibold text-gray-900 dark:text-gray-100">
            {step.name}
          </h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>
            {locked ? 'Close' : 'Cancel'}
          </Button>
          {!locked && (
            <Button size="sm" onClick={onSave} className="bg-orange-500 text-white hover:bg-orange-600">
              Save step
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-6 px-6 py-5">
        {/* Banner — example vs system */}
        {step.isExample && (
          <p className="rounded-xl bg-orange-50 px-4 py-3 text-xs leading-relaxed text-gray-700 dark:bg-orange-950/20 dark:text-gray-300">
            <strong className="text-orange-700 dark:text-orange-400">
              New {step.type} step — example.
            </strong>{' '}
            This is what creating a {step.type} step looks like, pre-filled to show the shape. Adjust
            the fields and Save to add it to the module.
          </p>
        )}
        {locked && (
          <p className="rounded-xl bg-gray-100 px-4 py-3 text-xs leading-relaxed text-gray-600 dark:bg-gray-800 dark:text-gray-300">
            This is a system-controlled step. It appears in the editor for ordering and context; copy,
            fields, and behavior are engineering-controlled and can&apos;t be edited here.
          </p>
        )}

        {/* Placement & visibility — identical for every type */}
        <section>
          <PanelSectionLabel>Placement &amp; visibility</PanelSectionLabel>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div>
              <FieldLabel>Module</FieldLabel>
              <Select defaultValue={step.moduleId} disabled={locked}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {modules.map((module) => (
                    <SelectItem key={module.id} value={module.id}>
                      {module.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <FieldLabel>Requirement</FieldLabel>
              <RequirementToggle value={step.requirement} disabled={locked} />
            </div>
          </div>

          <div className="mt-4">
            <FieldLabel>Routes</FieldLabel>
            <div className="flex flex-wrap gap-2">
              {ACTIVATION_ROUTES.map((route) => (
                <CheckChip
                  key={route.code}
                  label={route.label}
                  checked={step.routes.includes(route.code)}
                  disabled={locked}
                />
              ))}
            </div>
          </div>

          <div className="mt-4">
            <FieldLabel>Pathways</FieldLabel>
            <div className="flex flex-wrap gap-2">
              {ACTIVATION_PATHWAYS.map((pathway) => (
                <CheckChip
                  key={pathway}
                  label={pathway}
                  checked={!step.pathways || step.pathways.includes(pathway)}
                  disabled={locked}
                />
              ))}
            </div>
          </div>

          <div className="mt-4">
            <FieldLabel>Plan tiers</FieldLabel>
            <div className="flex flex-wrap gap-2">
              {ACTIVATION_PLANS.map((plan) => (
                <span
                  key={plan}
                  className={cn(
                    'rounded-full px-3 py-1 text-[11px] font-semibold',
                    plans.includes(plan)
                      ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                      : 'border border-gray-200 bg-gray-100 text-gray-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500',
                  )}
                >
                  {plan}
                </span>
              ))}
            </div>
          </div>
        </section>

        <VisibilityRuleBuilder
          rules={rules}
          join={join}
          onJoinChange={setJoin}
          onRulesChange={setRules}
          readOnly={locked}
        />

        {/* Type-specific body */}
        {locked ? (
          <SystemBody step={step} />
        ) : step.type === 'Form' ? (
          <FormBody step={step} />
        ) : step.type === 'Form — AI' ? (
          <AiFormBody step={step} />
        ) : step.type === 'Add-On' ? (
          <AddOnBody step={step} />
        ) : (
          <SimpleBody step={step} />
        )}

        <StepEmailsPanel emails={step.emails ?? []} readOnly={locked} />

        {/* Footer */}
        <div className="flex flex-wrap items-center gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          {!locked && (
            <Button
              variant="outline"
              size="sm"
              onClick={onArchive}
              className="border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300"
            >
              Archive step
            </Button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onCancel}>
              {locked ? 'Close' : 'Cancel'}
            </Button>
            {!locked && (
              <Button size="sm" onClick={onSave} className="bg-orange-500 text-white hover:bg-orange-600">
                Save step
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivationStepEditor;
